import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import logo from '@/assets/logo.webp';

const Register = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [studioName, setStudioName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Make the API call to trigger a contact us
      const response = await fetch(`${import.meta.env.VITE_ADMIN_CONTACTUS_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, name, studioName }),
      });

      if (!response.ok) {
        throw new Error("Failed to send contact us");
      }

      toast({
        title: t("contactUs.requestSent"),
        description: t("contactUs.weWillGetBack"),
        className: "bg-green-500 text-white border-green-600",
        duration: 5000,
      });
      // ✅ Clear fields after success
      setName("");
      setEmail("");
      setStudioName("");
    } catch (error) {
      console.error("Contact us failed:", error);
      toast({
        title: t("contactUs.toastErrorTitle"),
        description: t("contactUs.toastErrorDescription"),
        variant: "destructive",
        duration: 5000,
      });
    }
    finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.title = `${t('signup.title')} - FitCalendar`;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md ">
        <div className="flex flex-col items-center">
          <a href="/"><img src={logo} alt="FitCalendar" className="h-6 object-contain mt-8 mb-8" /></a>
        </div>
        <CardHeader>
          <CardTitle>{t('register.title')}</CardTitle>
          <CardDescription>
            {t('register.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("register.name")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("register.enterName")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("register.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("register.enterEmail")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="studioName">{t("register.studioName")}</Label>
              <Input
                id="studioName"
                value={studioName}
                onChange={(e) => setStudioName(e.target.value)}
                placeholder={t("register.enterStudioName")}
                required
              />
            </div>
            <Alert variant="destructive" className="bg-orange-50">
              <AlertDescription>
                {t("register.alertStudioProfessional")}
              </AlertDescription>
            </Alert>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t("register.submitting") : t("register.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;